import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { HostType, OnPremDatabaseOutput, OnPremOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import PreviewBanner from '../../../common/PreviewBanner';
import { commonTotalCostSummary, generateCashFlowTable, summaryColumnDefs } from '../../common/Common';
import HostTypeConfiguration from '../../common/HostTypeConfiguration';
import { CommonSummaryItem } from '../../common/types';
import { updateTCOAssessment } from '../../TCOHelper';
import {
    buildOnPremCashFlowItems,
    buildOnPremisesTabs,
    buildSummaryItems,
    OnPremEnvironment,
    OnPremFacilitiesTable,
    OnPremRackInfrastructureTable,
    OnPremSoftwareLicenseTable,
    OnPremVirtualHostTable,
} from '../OnPremisesHelper';
import { OnPremDatabaseLicenseTable, OnPremDatabasePhysicalServerTable } from './OnPremDatabaseCostHelper';

type Props = FlashContextInterface;
const tcoService = new TcoService();
function OnpremDatabaseCost({ flash }: Props): JSX.Element {
    const [ loading, setLoading ] = useState(false);
    const { assessment, assessmentToUpdate, tcoLoading, updateAssessmentToUpdate, updateTcoLoading } = useContext(TcoContext);
    const [ onPremiseOutput, setOnPremiseOutput ] = useState<OnPremOutput | null>(null);
    const [ activeTabId, setActiveTabId ] = useState('physicalServers');
    const [ hostTypeConfigVisible, setHostTypeConfigVisible ] = useState(false);
    const [ onPremHostType, setOnPremHostType ] = useState<HostType[] | undefined>(undefined);
    const [ isColocation, setIsColocation ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const isMounted = useIsMounted();
    const loadOnPremiseData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const onPremAssessment = await tcoService.getOnPremise(tcoAssessmentId);
                if (isMounted()) {
                    setOnPremiseOutput(onPremAssessment.output.onPremises);
                    setOnPremHostType(onPremAssessment.input.onPremises.hostTypes);
                    setIsColocation(onPremAssessment.configuration.tcoAssumptions.onpremEnvironment === OnPremEnvironment.COLO);
                }
            }
        } catch(err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_ONPREM);
        } finally {
            if (isMounted()) { setLoading(false); }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ONPREM_DATABASE_COST);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadOnPremiseData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    // Summary table
    const buildDatabaseSummaryItems = (): CommonSummaryItem[] => {
        if (!onPremiseOutput?.database) { return []; }
        return buildSummaryItems(onPremiseOutput.database);
    };

    const saveHostTypes = async (hostTypes: HostType[]): Promise<void> => {
        try {
            updateTcoLoading(true);
            if (assessmentToUpdate && portfolioId) {
                assessmentToUpdate.input.onPremises.hostTypes = hostTypes;
                const newTcoAssessment = await updateTCOAssessment(portfolioId, assessmentToUpdate, tcoService);
                updateAssessmentToUpdate(newTcoAssessment);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_ASSESSMENT);
        } finally {
            updateTcoLoading(false);
        }
    };
    
    const buildTabs = (database: OnPremDatabaseOutput, currency: string): TabsProps.Tab[] => {
        const virtualHostContent = (
            <SpaceBetween size='l' direction='vertical'>
                <Button variant='primary' onClick={() => setHostTypeConfigVisible(true)}> {constants.ON_PREM_CONFIGURE_HOST_TYPES} </Button>
                {hostTypeConfigVisible && onPremHostType && (
                    <HostTypeConfiguration
                        visible={hostTypeConfigVisible}
                        setVisible={setHostTypeConfigVisible}
                        hostTypes={onPremHostType}
                        saveHostTypes={saveHostTypes}
                    />
                )}
                {OnPremVirtualHostTable(database.virtual.hosts, currency)}
            </SpaceBetween>
        );

        const licensesTables = (
            <SpaceBetween size='l' direction='vertical'>
                { OnPremSoftwareLicenseTable(database.software, currency) }
                { OnPremDatabaseLicenseTable(database.databaseLicenseCost.licenseCost, currency) }
            </SpaceBetween>
        );
        
        return buildOnPremisesTabs(
            OnPremDatabasePhysicalServerTable(database.physical.servers, currency),
            virtualHostContent,
            OnPremRackInfrastructureTable(database.hardware, currency),
            licensesTables,
            OnPremFacilitiesTable(database.facilities, currency, isColocation),
            generateCashFlowTable(buildOnPremCashFlowItems(database.cashFlow), database.cashFlow.yearly.total.length, currency),
        );
    };

    const buildOnPremDatabasePageContent = (): JSX.Element => {
        if (loading || tcoLoading || !onPremiseOutput) {
            return (<Spinner size='big' />);
        }

        const database = onPremiseOutput.database;
        const currency = onPremiseOutput.currency;
        return (
            <SpaceBetween size='xl'>
                <PreviewBanner customText={constants.RDS_PREVIEW_TEXT} />
                <Header variant='h1'>{constants.ON_PREM_DATABASE_TITLE}</Header>
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildDatabaseSummaryItems()}
                    sortingDisabled
                />
                {commonTotalCostSummary(currency, database.totalCost, database.manualAdjustment.value, database.adjustedTotalCost)}
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs tabs={buildTabs(database, currency)} activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)}></Tabs>
            </SpaceBetween>
        );
    };

    return (
        <Container data-testid='container'>
            {buildOnPremDatabasePageContent()}
        </Container>
    );
}

export default withFlashContext(OnpremDatabaseCost);
